'use client';

import React, { createContext, useContext, useEffect, useMemo, useSyncExternalStore } from 'react';
import type { PrivilegeScope } from '@/core/be/security/SecurityTypes';
import type { Role } from '@prisma/client';

export interface SecurityContextValue {
  loading: boolean;
  profileId: string | null;
  role: Role | null;
  privileges: {
    global: Set<string>;
    vault: Set<string>;
  };
  hasPrivilege: (privilegeId: string, scope?: PrivilegeScope) => boolean;
}

const emptySets = {
  global: new Set<string>(),
  vault: new Set<string>(),
};

const defaultContext: SecurityContextValue = {
  loading: false,
  profileId: null,
  role: null,
  privileges: emptySets,
  hasPrivilege: () => false,
};

export const SecurityContext = createContext<SecurityContextValue>(defaultContext);

// Global bridge for components outside the React context tree
// This stores the current security context value and notifies subscribers when it changes
let globalSecurityValue: SecurityContextValue = defaultContext;
const listeners = new Set<() => void>();

function setGlobalSecurityValue(value: SecurityContextValue) {
  globalSecurityValue = value;
  listeners.forEach((listener) => listener());
}

function subscribeToGlobalSecurity(callback: () => void) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function getGlobalSecuritySnapshot() {
  return globalSecurityValue;
}

export interface SecurityProviderProps {
  children: React.ReactNode;
  privileges?: {
    global?: string[];
    vault?: string[];
  };
  loading?: boolean;
  profileId?: string | null;
  role?: Role | null;
}

export function SecurityProvider({
  children,
  privileges,
  loading = false,
  profileId = null,
  role = null,
}: SecurityProviderProps) {
  const sets = useMemo(
    () => ({
      global: new Set(privileges?.global ?? []),
      vault: new Set(privileges?.vault ?? []),
    }),
    [privileges?.global, privileges?.vault],
  );

  const value = useMemo<SecurityContextValue>(() => ({
    loading,
    profileId,
    role,
    privileges: sets,
    hasPrivilege: (privilegeId: string, scope: PrivilegeScope = 'global') => {
      if (scope === 'vault') {
        return sets.vault.has(privilegeId);
      }
      return sets.global.has(privilegeId);
    },
  }), [loading, profileId, role, sets]);

  // Update global bridge when context value changes
  useEffect(() => {
    setGlobalSecurityValue(value);
    return () => {
      // Reset to default when provider unmounts
      setGlobalSecurityValue(defaultContext);
    };
  }, [value]);

  return <SecurityContext.Provider value={value}>{children}</SecurityContext.Provider>;
}

export function useSecurity(): SecurityContextValue {
  // First try to get from React context
  const contextValue = useContext(SecurityContext);

  // Subscribe to global bridge for components outside the context tree
  const globalValue = useSyncExternalStore(
    subscribeToGlobalSecurity,
    getGlobalSecuritySnapshot,
    getGlobalSecuritySnapshot
  );

  // If context value is the default (no provider), use global value
  // This handles the case where component is rendered outside SecurityProvider
  if (contextValue === defaultContext && globalValue !== defaultContext) {
    return globalValue;
  }

  return contextValue;
}
